import React from 'react';
import PropTypes from 'prop-types';

import {Box, Divider, Stack, StickyTooltip, Typography, screenSizeBigger} from 'views/components';
import {ConnectionBadge, HoverToolBox} from 'views/module';
import DatePickerQuickOptionCalendar from '../../../../module/dates/DatePickerQuickOptionCalendar';
import CommandTextViewer from '../../../../module/task/components/CommandTextViewer';

import moment from 'moment';

const TaskItem = (props) => {
  const {
    task,
    onClickEditTask,
    onUpdateDueDate,
    removeTask,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const maxDisplayedAvatarIcons = 5;

  const dueDate = moment(task.dueDate, moment.ISO_8601, true);
  const isDueDateADate = dueDate.isValid();
  const isToday = isDueDateADate ? dueDate.isSame(moment(), 'day') : null;
  const isOverdue = isDueDateADate ? dueDate.isBefore(moment()) : false;
  const connections = task.connectionInfo || [];
  const ownerRns = Array.isArray(task.ownerRns) ? task.ownerRns :
    Array.isArray(task.ownerRn) ? task.ownerRn :
      task.ownerRn ? [task.ownerRn] : [];
  const owners = (task.ownerInfo || []).length > 0 ?
    task.ownerInfo :
    ownerRns
        .filter(Boolean)
        .map((ownerRn) => ({userRn: ownerRn, name: ownerRn}));
  const visibleConnections = connections.slice(0, maxDisplayedAvatarIcons);
  const hiddenConnections = connections.slice(maxDisplayedAvatarIcons);

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isDueDatePickerOpen, setIsDueDatePickerOpen] = React.useState(false);
  const rightEdgeAnchorRef = React.useRef(null);
  const dueDateButtonRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };
  const handleDueDateClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isStatusComplete) {
      return;
    }

    setIsDueDatePickerOpen(true);
  };
  const closeDueDatePicker = () => {
    setIsDueDatePickerOpen(false);
  };
  const handleDueDateChange = (nextValue) => {
    if (isStatusComplete) {
      return;
    }

    const parsedValue = nextValue ? moment(nextValue) : null;
    const nextDueDateIso = parsedValue?.isValid?.() ? parsedValue.toISOString() : null;
    const currentDueDateIso = isDueDateADate ? dueDate.toISOString() : null;

    if (nextDueDateIso === currentDueDateIso) {
      return;
    }

    if (onUpdateDueDate) {
      onUpdateDueDate(nextDueDateIso);
    }
  };

  const getConnectionDisplayName = (connection) => [connection.firstName, connection.lastName].filter(Boolean).join(' ');

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  return (
    <Box
      sx={{
        display: 'contents',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          minWidth: 0,
          py: 1.5,
          opacity: isStatusComplete ? 0.45 : 1,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: isStatusComplete ? 'success.main' : (isOverdue ? 'error.main' : 'grey.500'),
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 400,
            minWidth: 0,
            textDecoration: isStatusComplete ? 'line-through' : 'none',
          }}
        >
          <CommandTextViewer
            value={task.title}
            owners={owners}
            connections={connections}
            type={task.type}
          />
        </Typography>
      </Stack>
      <Box
        sx={{
          minWidth: 0,
          py: 1.5,
          opacity: isStatusComplete ? 0.45 : 1,
        }}
      >
        {
          connections.length > 0 &&
            <Stack
              direction="row"
              spacing={-.5}
              sx={{
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              {
                visibleConnections.map((connection, index) => (
                  <ConnectionBadge
                    key={connection.connectionRn}
                    connection={connection}
                    sx={{
                      width: '30px',
                      height: '30px',
                      position: 'relative',
                      zIndex: index + 1,
                      fontSize: '16px',
                      outline: '2px solid',
                      outlineColor: 'background.default',
                    }}
                  />
                ))
              }
              {
                hiddenConnections.length > 0 &&
                  <StickyTooltip
                    title={
                      <Stack spacing={0.5}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                          }}
                        >
                          {
                            hiddenConnections.map((connection) => getConnectionDisplayName(connection)).join(', ')
                          }
                        </Typography>
                      </Stack>
                    }
                  >
                    <Box
                      sx={{
                        width: '30px',
                        height: '30px',
                        position: 'relative',
                        zIndex: visibleConnections.length + 1,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#E2DEDA',
                        color: 'text.secondary',
                        fontSize: '12px',
                        fontWeight: 600,
                        outline: '2px solid',
                        outlineColor: 'background.default',
                      }}
                    >
                      +{hiddenConnections.length}
                    </Box>
                  </StickyTooltip>
              }
            </Stack>
        }
      </Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-end"
        sx={{
          position: 'relative',
          flexShrink: 0,
          textAlign: 'right',
          py: 1.5,
          opacity: isStatusComplete ? 0.45 : 1,
        }}
      >
        {
          connections.length > 0 &&
            <Divider
              orientation='vertical'
              flexItem
            />
        }
        <Box
          ref={rightEdgeAnchorRef}
          sx={{
            position: 'absolute',
            right: -100,
            top: -7.5,
            width: 0,
            height: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: -120,
            top: 0,
            width: 120,
            height: '100%',
          }}
        />
        <Stack
          alignItems="flex-end"
          sx={{
            minWidth: 0,
          }}
        >
          {
            isDueDateADate &&
            <Box
              component='button'
              ref={dueDateButtonRef}
              onClick={handleDueDateClick}
              sx={{
                border: 'none',
                background: 'none',
                p: 0,
                cursor: isStatusComplete ? 'default' : 'pointer',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isStatusComplete ? 'success.main' : (isOverdue ? 'error.main' : 'grey.500'),
                  whiteSpace: 'nowrap',
                }}
              >
                {isToday ? dueDate.format('h:mmA') : dueDate.format('MMM D')}
              </Typography>
            </Box>
          }
          {
            (task.dueDate && !isDueDateADate) &&
            <Box
              component='button'
              ref={dueDateButtonRef}
              onClick={handleDueDateClick}
              sx={{
                border: 'none',
                background: 'none',
                p: 0,
                cursor: isStatusComplete ? 'default' : 'pointer',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isStatusComplete ? 'success.main' : (isOverdue ? 'error.main' : 'grey.500'),
                  whiteSpace: 'nowrap',
                }}
              >
                {task.dueDate}
              </Typography>
            </Box>
          }
        </Stack>
      </Stack>
      <DatePickerQuickOptionCalendar
        open={isDueDatePickerOpen}
        anchorRef={dueDateButtonRef}
        onClose={closeDueDatePicker}
        onChange={handleDueDateChange}
        value={task.dueDate}
      />
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        onClickEdit={() => onClickEditTask(task)}
        onClickDelete={() => removeTask(task)}
        hideTool={{
          emoji: true,
          copy: true,
          edit: false,
          delete: false,
          reply: true,
        }}
      />
      <Divider sx={{gridColumn: '1 / -1'}} />
    </Box>
  );
};

TaskItem.propTypes = {
  task: PropTypes.shape({
    taskRn: PropTypes.string.isRequired,
    type: PropTypes.string,
    title: PropTypes.string.isRequired,
    ownerRn: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    ownerRns: PropTypes.arrayOf(PropTypes.string),
    connections: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        connectionRn: PropTypes.string,
      }),
    ])),
    connectionInfo: PropTypes.arrayOf(PropTypes.shape({
      connectionRn: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    })),
    ownerInfo: PropTypes.arrayOf(PropTypes.shape({
      userRn: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
    })),
    dueDate: PropTypes.string,
    status: PropTypes.string.isRequired,
  }).isRequired,
  onClickEditTask: PropTypes.func,
  onUpdateDueDate: PropTypes.func,
  removeTask: PropTypes.func,
};

export default React.memo(TaskItem);
